import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useToast } from '../hooks/useToast';
import { useBusy, useBusyKeys } from '../hooks/useBusy';
import { useConfirm } from '../hooks/useConfirm';
import { useUrlTab } from '../hooks/useUrlParam';
import { useQueue, type Queue } from '../hooks/useQueue';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../i18n';
import { useLabels } from '../i18n/labels';
import { usePageTitle } from '../hooks/usePageTitle';
import Avatar from '../components/shared/Avatar';
import ClaimCheckBadge from '../components/shared/ClaimCheckBadge';
import ModalBackdrop from '../components/shared/ModalBackdrop';
import ModalHeader from '../components/shared/ModalHeader';
import ModalFooter from '../components/shared/ModalFooter';
import PingModal from '../components/shared/PingModal';
import TabBar from '../components/shared/TabBar';
import ResolveActions, { RESOLVE_ACTIONS_CLS } from '../components/shared/ResolveActions';
import RoleBadge from '../components/shared/RoleBadge';
import StatusBadge from '../components/shared/StatusBadge';
import FilterBar from '../components/shared/FilterBar';
import { PencilIcon, BellIcon, MailIcon, PeopleIcon, PersonIcon, BookIcon, ChevronLeftIcon, ChevronRightIcon } from '../components/shared/Icons';
import Skeleton from '../components/shared/Skeleton';
import EmptyState from '../components/shared/EmptyState';
import AsyncContent from '../components/shared/AsyncContent';
import { errorCode, errorStatus } from '../lib/httpError';
import { logBackgroundError } from '../observability';
import { useLoad } from '../hooks/useLoad';
import api, { getPendingTeamClaims, getStaffTeamInvites, resolveStaffTeamInvite, resolveTeamClaim, getAdminPendingMembers, resolveAdminPendingMember,
  getPendingAthleteClaims, resolveClaimRequest, getAdminPendingCounts, getRoleRequests, getUsers, resolveAthleteRequest,
  type AdminPendingCounts } from '../api/client';
import { apiError } from '../hooks/useApiCall';
import type { UserListRow, RoleRequestRow, TeamClaim, Role, TeamMemberRole, AthleteTeamRequest, AthleteTeamInvite, AdminAthleteClaim } from '../types';
import { toLocalDay } from '../utils/calendarDate';
import { personName, nameMatches, textMatches } from '../utils/personName';
import { weightText } from '../utils/weight';
import { labelFor } from '../lib/auditLabels';
import { announcePendingCounts } from '../lib/pendingCounts';
import { BADGE_XS } from '../components/shared/badgeSize';
import Field from '../components/shared/Field';

type AdminTab = 'requests' | 'users' | 'audit';

/** Team-member request awaiting admin approval — returned by
 *  GET /api/users/admin/pending-members. */
interface PendingMemberRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  photo_url?: string;
  team_name: string;
  role: TeamMemberRole;
  joined_at: string;
}

/** Bookkeeping columns that are never a CHANGE to show. Mirrors
 *  `NOT_A_CHANGE` in backend `services/audit.ts` (#405): the backend stops
 *  a no-op save from writing a row at all, and this stops a real change
 *  from being buried under a timestamp line the reader must skip. The
 *  INSERT and DELETE blocks below have always filtered the same names. */
const NOT_A_CHANGE = new Set(['updated_at', 'operation', 'claimed_at', 'marked_at']);

/** Audit-log row — shape mirrors GET /api/users/admin/audit-log rows. Values
 *  are typed as Record<string, unknown> because the table is polymorphic;
 *  callers stringify per key rather than reading specific columns. */
interface AuditLogRow {
  id: string;
  table_name: string;
  row_id: string;
  /** The changed row's name, resolved by the backend (#347); null for an unknown table. */
  row_label?: string | null;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  /** The actor is named by the email alone (#389). The Log Analytics `audit`
   *  event carries no name, and `req.user` holds only id, email and roles —
   *  so resolving one would put the database back in this read path. */
  user_email: string | null;
  user_id?: string | null;
  ip_address?: string;
  old_values?: Record<string, unknown>;
  new_values?: Record<string, unknown>;
  /** The readable name of each foreign key in the payload, keyed by role
   *  (`tournament`, `athlete`, `bout`, …). Captured at WRITE time, so a
   *  renamed row keeps the name it had when the change happened. Empty on
   *  the Postgres path, which has no column for it (#389). */
  labels?: Record<string, string>;
  /** The catalogue name of the event (`sign_in_failed`, `bout_updated`);
   *  '' for an event written without one. Both sinks return it. */
  event?: string;
  created_at: string;
}

const ALL_ROLES =['admin', 'tournament_manager', 'team_manager', 'head_coach', 'coach', 'athlete', 'referee', 'jury'];

/** #552 — the user list shows this many rows, and "Show more" adds this many. */
const USERS_PAGE_SIZE = 100;

/** #552 — one date format on the whole page: the day of an instant as
 *  'YYYY-MM-DD', in the browser's timezone. The request cards showed
 *  '9/27/2026' and the user rows '2026-09-27'. */
const day = (iso: string) => toLocalDay(new Date(iso));
/** The same day plus 'HH:MM', for the audit rows. */
const dayTime = (iso: string) => {
  const d = new Date(iso);
  return `${toLocalDay(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** #552 — a button of the request section switcher. On a phone the
 *  switcher is one strip that scrolls sideways, so a label never wraps. From
 *  sm up the strip wraps: a tablet showed no hint that it scrolled. */
const sectionButton = (active: boolean) =>
  `shrink-0 sm:flex-1 max-sm:whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
    active ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-700'}`;

/** #552 — a request card. On a phone the Approve and Reject buttons take
 *  their own line under the text, so the text keeps the width of the card. */
const REQUEST_CARD = 'card p-4 flex flex-wrap sm:flex-nowrap items-start gap-3 sm:gap-4';

/** The sub-tabs of the Requests tab. 'all' is the merged list (#376). */
type RequestsSection = 'all' | 'roles' | 'claims' | 'members' | 'athletes' | 'athleteClaims';
/** The kinds of pending request. Each kind has one entry in the QUEUES table of the page. */
type RequestKind = 'role' | 'teamClaim' | 'member' | 'athleteJoin' | 'athleteClaim' | 'athleteInvite';

/** One request, in the shape of its card (#664, F68). The page maps each kind
 *  to this shape ONCE. The merged list, the list of each kind and the role
 *  history all draw it with RequestCard, so a kind has one description. */
interface RequestRow {
  key: string;
  /** The name of the kind. The QUEUES table sets it. The role history shows none. */
  kindLabel?: string;
  at: string;
  first: string;
  last: string;
  email?: string;
  photo?: string;
  /** What the person asks for, already in the reader's language. */
  summary: string;
  /** #569 — the marks for the reviewer: the checks of a claim, the roles of a person. */
  marks?: ReactNode;
  /** More lines under the ask: the note of a role request, the data of an athlete. */
  details?: ReactNode;
  /** #569 — set on the rows of a claim: a spec finds the card by it. */
  claimId?: string;
  /** A status other than 'pending' shows a badge in place of the buttons (the role history). */
  status?: string;
  resolvedAt?: string;
  resolve: (status: 'approved' | 'rejected') => void;
}

/** One entry of the QUEUES table: a kind of request, its list and its place in the switcher. */
interface QueueDef {
  kind: RequestKind;
  kindLabel: string;
  /** The sub-tab of the kind. A kind with no section shows in the merged list only. */
  section?: RequestsSection;
  sectionLabel?: string;
  /** The server zeroes the role queue for a team manager, and the page does not read it. */
  adminOnly?: boolean;
  queue: Pick<Queue<unknown>, 'loading' | 'failed' | 'reload'>;
  rows: RequestRow[];
  /** The empty state of the list of the kind. Set when the section draws it. */
  empty?: { icon: ReactNode; title: string; hint: string };
}

/** Build an entry of the table: the rows are the rows of the queue in the shape of the card. */
function queueDef<T>(d: Omit<QueueDef, 'rows' | 'queue'> & { queue: Queue<T>; toRow: (item: T) => RequestRow }): QueueDef {
  const { toRow, ...def } = d;
  return { ...def, rows: d.queue.rows.map(item => ({ ...toRow(item), kindLabel: d.kindLabel })) };
}

/** The red count on a button of the Requests tab. */
function CountDot({ n }: { n: number }) {
  return n > 0
    ? <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{n}</span>
    : null;
}

/** #664 (F68) — the card of one pending request. The kind badge shows in the
 *  merged list only (`showKind`). A resolved request shows its status in place
 *  of the buttons. Every text is a `t.*` text or comes from the row. */
function RequestCard({ row, showKind, busy }: { row: RequestRow; showKind: boolean; busy: boolean }) {
  const { t } = useI18n();
  const resolved = !!row.status && row.status !== 'pending';
  return (
    <div data-testid="request-card" data-claim-id={row.claimId} className={REQUEST_CARD}>
      <Avatar firstName={row.first} lastName={row.last} photoUrl={row.photo} size="md" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
          {showKind && row.kindLabel && <span data-testid="request-kind" className={`badge badge-amber ${BADGE_XS} uppercase whitespace-nowrap`}>{row.kindLabel}</span>}
          <span className="font-semibold text-sm">{personName(row.first, row.last)}</span>
          {row.email && <span className="text-xs text-gray-400 truncate max-w-full">{row.email}</span>}
        </div>
        <div className="text-sm text-gray-600 mt-1">{row.summary}</div>
        {row.marks && <div className="mt-1 flex flex-wrap gap-1">{row.marks}</div>}
        {row.details && <div className="text-xs text-gray-500 mt-1">{row.details}</div>}
        {row.at && (
          <div className="text-xs text-gray-400 mt-1">
            {t.admin.requestedOn.replace('{date}', day(row.at))}
            {row.resolvedAt && ` · ${t.admin.resolvedOn.replace('{date}', day(row.resolvedAt))}`}
          </div>
        )}
      </div>
      {resolved
        ? <div className={RESOLVE_ACTIONS_CLS}><StatusBadge status={row.status!} /></div>
        : <ResolveActions busy={busy} onResolve={row.resolve} />}
    </div>
  );
}

/** A list of request cards. `isBusy` is `resolving.isBusy` of `useBusyKeys()`: one lock for each row (#605). */
function RequestList({ rows, showKind = false, isBusy }: { rows: RequestRow[]; showKind?: boolean; isBusy: (key: string) => boolean }) {
  return (
    <div className="space-y-3">
      {rows.map(row => <RequestCard key={row.key} row={row} showKind={showKind} busy={isBusy(row.key)} />)}
    </div>
  );
}

/** One value of a payload, with its name beside it when there is one. */
function AuditValue({ k, v, labels }: { k: string; v: unknown; labels?: Record<string, string> }) {
  const name = labelFor(k, labels);
  return (
    <div>
      <span className="text-gray-400">{k}:</span> {JSON.stringify(v, null, 2)}
      {name && <span className="ml-1 text-indigo-600 font-semibold">→ {name}</span>}
    </div>
  );
}

export default function Admin() {
  const { hasRole } = useAuthStore();
  const { t } = useI18n();
  const { genderLabel, ageShort, boutCount, entryCount, roleLabel, requestStatusLabel } = useLabels();
  // Tab state is URL-driven (mirrors TournamentDetail): `?tab=<slug>` persists
  // across reloads and is shareable. Visibility is role-scoped: TMs cannot
  // see `users` (admin-only endpoint) or `audit` (admin-only endpoint), so
  // those slugs fall through to `requests` for non-admin callers.
  const isAdmin = hasRole('admin');
  const allowedTabs: readonly AdminTab[] = isAdmin
    ? ['users', 'requests', 'audit']
    : ['requests'];
  const defaultTab: AdminTab = isAdmin ? 'users' : 'requests';
  // #595 — the shared tab rule: an unknown or forbidden slug shows the default.
  const [tab, setTab] = useUrlTab(allowedTabs, defaultTab);
  const tabTitleByTab: Record<AdminTab, string> = {
    users:    t.admin.users,
    requests: t.admin.requests,
    audit:    t.admin.auditLog,
  };
  usePageTitle([tabTitleByTab[tab], t.admin.panelTitle]);

  // The role history of the Roles sub-tab follows `reqFilter`, so it holds
  // approved or rejected rows too (roleHistoryQ, below). The pending role
  // requests are a queue of their own (roleQ): the merged queue must not follow
  // this filter, or it drops every pending role request until the admin selects
  // Pending again.
  const [reqFilter, setReqFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');

  // Users
  // #665, F02 — the read of the users is `usersQ`, below (it needs `tab`).
  const [userSearch, setUserSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  // #552 — the list renders the first page only; the search and the role
  // filter read the full list, and a new search starts again at one page.
  const [shownUsers, setShownUsers] = useState(USERS_PAGE_SIZE);
  useEffect(() => { setShownUsers(USERS_PAGE_SIZE); }, [userSearch, roleFilter]);
  const [editingUser, setEditingUser] = useState<UserListRow | null>(null);
  const [editRoles, setEditRoles] = useState<string[]>([]);
  // Identity-only post users-athlete-split. Athlete attrs go via /api/athletes/:id (separate UI surface).
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [showCreateUser, setShowCreateUser] = useState(false);
  // #552 — Create User is a form: Enter submits it, so a busy flag stops a second POST.
  // #605 — the flag is the lock of useBusy: its ref stops also two taps in one task.
  const userCreate = useBusy();
  const [createForm, setCreateForm] = useState({ email: '', first_name: '', last_name: '', password: '', phone: '', roles: [] as string[] });

  // Audit log
  const [auditPage, setAuditPage] = useState(0);
  const [auditTable, setAuditTable] = useState('');
  /** #503 — the session refreshes hidden (about one row an hour for each
   *  person who uses the app). The security events are in the same list. */
  const [auditHideRefreshes, setAuditHideRefreshes] = useState(false);
  // #457 — the input changes on each key; the query waits 400 ms for the
  // typing to stop. Before, each key sent one Log Analytics query.
  const [auditSearchInput, setAuditSearchInput] = useState('');
  const [auditSearch, setAuditSearch] = useState('');
  // The empty hint says when a filter hides the rows (frontend.md, Lessons).
  const auditFilterOn = !!auditTable || !!auditSearch || auditHideRefreshes;
  useEffect(() => {
    if (auditSearchInput === auditSearch) return;
    const id = setTimeout(() => { setAuditSearch(auditSearchInput); setAuditPage(0); }, 400);
    return () => clearTimeout(id);
  }, [auditSearchInput]);
  const [auditDetail, setAuditDetail] = useState<AuditLogRow | null>(null);

  const { showError, showSuccess, showWarning } = useToast();
  const { confirm } = useConfirm();
  // #664 (F40) — the user that the admin pings. PingModal holds the text and the lock (#605).
  const [pingUser, setPingUser] = useState<{ id: string; first_name: string } | null>(null);
  // #605 — one answer at a time for each request. Approve and Reject of a row
  // share the lock. The key is the row key of the merged queue, so the merged
  // queue and the section of the kind share it too.
  const resolving = useBusyKeys();
  // #605 — Save and the erase of the Edit user modal share one lock.
  const userEdit = useBusy();

  // Unified requests section filter. TMs cannot see `roles` (admin-only
  // endpoint /admin/role-requests) — default them to `claims`.
  // 'all' is the merged queue (#376): every pending request, oldest first.
  // The per-type views stay behind the other buttons.
  const [requestsSection, setRequestsSection] = useState<RequestsSection>('all');

  // The six queues of pending requests (#664, F16). Each one is a list with
  // its loading flag and its failed flag (hooks/useQueue.ts). The QUEUES table
  // below holds what the page knows about a kind.
  //   - The PENDING role requests are fetched on their own: `requests` follows
  //     the filter of the Roles sub-tab. The role queue is for admins only.
  //   - Athlete record claims (#376): the coaches do not use the application,
  //     so the staff resolve these.
  //   - #658 — the team invitations of records with no account: staff approve
  //     for the athlete.
  const roleQ = useQueue<RoleRequestRow>(
    () => getRoleRequests('pending').then(r => r.data),
    { source: 'Admin:pendingRoles', enabled: isAdmin });
  // The role history reads under the same guard as the queues: a late answer of
  // an older filter, or of a read before an answer of the admin, is not drawn.
  const roleHistoryQ = useQueue<RoleRequestRow>(
    () => getRoleRequests(reqFilter !== 'all' ? reqFilter : undefined).then(r => r.data),
    { source: 'Admin:roleHistory', enabled: isAdmin });
  const teamClaimQ = useQueue<TeamClaim>(() => getPendingTeamClaims().then(r => r.data), { source: 'Admin:teamClaims' });
  // api/client declares the payload as Record<string, unknown>[] — narrow at the boundary.
  const memberQ = useQueue<PendingMemberRow>(
    () => getAdminPendingMembers().then(r => r.data as unknown as PendingMemberRow[]),
    { source: 'Admin:members' });
  const athleteJoinQ = useQueue<AthleteTeamRequest>(
    () => api.get<AthleteTeamRequest[]>('/api/teams/admin/athlete-requests').then(r => r.data),
    { source: 'Admin:athleteRequests' });
  const athleteClaimQ = useQueue<AdminAthleteClaim>(() => getPendingAthleteClaims().then(r => r.data), { source: 'Admin:athleteClaims' });
  const athleteInviteQ = useQueue<AthleteTeamInvite>(() => getStaffTeamInvites().then(r => r.data), { source: 'Admin:athleteInvites' });

  // The number on the Requests tab is the number of the server (GET
  // /api/users/admin/pending-counts), the same one as the nav badge. The page
  // reads it with the queues and again after each answer. A late answer of an
  // older read is dropped.
  const [counts, setCounts] = useState<AdminPendingCounts | null>(null);
  const countsRead = useRef(0);
  async function loadCounts() {
    const run = ++countsRead.current;
    try {
      const r = await getAdminPendingCounts();
      if (run === countsRead.current) {
        setCounts(r.data);
        // The nav badge re-reads on a route change only: tell the Layout the new total.
        announcePendingCounts(r.data.total);
      }
    } catch (err) {
      logBackgroundError('Admin:pending-counts')(err);
      // The latest read failed. Drop the old number, so the tab shows the length of the lists.
      if (run === countsRead.current) setCounts(null);
    }
  }

  // #552 — the user list loads when the page opens, on every tab: the Users
  // tab label shows its count. It read "Users (0)" on the Requests and Audit
  // tabs. A return to the Users tab loads the list again (a request can
  // change the roles), but the first open loads it one time only.
  // #665, F02 — `usersVisit` is a key of the read: each return to the Users tab bumps it.
  const [usersVisit, setUsersVisit] = useState(0);
  const usersFirstTab = useRef(true);
  useEffect(() => {
    if (tab === 'users' && !usersFirstTab.current) setUsersVisit(v => v + 1);
    usersFirstTab.current = false;
  }, [tab]);
  // The list stays on screen during a new read. A team manager has no access
  // to the user list (403): that is no failure, and the list stays unloaded.
  const usersQ = useLoad(
    isAdmin ? () => getUsers().then(r => r.data).catch(err => {
      if (errorStatus(err) === 403) return undefined;
      throw err;
    }) : null,
    [usersVisit],
    { context: 'Admin:users', keepData: true },
  );
  const users = usersQ.data ?? [];
  // #552 — the tab label shows a count only after the list loaded: not "(0)".
  const usersLoaded = usersQ.data !== undefined;
  const setUsers = (next: (prev: UserListRow[]) => UserListRow[]) => usersQ.setData(prev => next(prev ?? []));

  // The audit log. The filters and the page are the key; the old rows stay on
  // screen during a new read, and only the newest answer shows.
  const auditQ = useLoad(
    tab === 'audit' ? async () => {
      const params: Record<string, string> = { limit: '100', offset: String(auditPage * 100) };
      if (auditTable) params.table = auditTable;
      if (auditSearch) params.search = auditSearch;
      if (auditHideRefreshes) params.hide_refreshes = '1';
      const r = await api.get('/api/users/admin/audit-log', { params });
      return {
        rows: r.data.rows as AuditLogRow[],
        total: r.data.total as number,
        /** Every table the environment has actually audited. The backend returns it with every page. */
        tables: Array.isArray(r.data.tables) ? r.data.tables as string[] : [],
      };
    } : null,
    [auditPage, auditTable, auditSearch, auditHideRefreshes],
    { context: 'Admin:audit', keepData: true, initial: { rows: [] as AuditLogRow[], total: 0, tables: [] as string[] } },
  );
  const auditLog = auditQ.data.rows;
  const auditTotal = auditQ.data.total;
  const auditTables = auditQ.data.tables;

  // The first run belongs to the [tab] effect below: it loads the role history with the queues.
  const reqFilterFirstRun = useRef(true);
  useEffect(() => {
    if (reqFilterFirstRun.current) { reqFilterFirstRun.current = false; return; }
    if (tab === 'requests') void roleHistoryQ.reload();
  }, [reqFilter]);

  function handleAthleteInvite(inv: AthleteTeamInvite, status: 'approved' | 'rejected') {
    return resolving.run(`athleteInvite:${inv.id}`, async () => {
      try {
        await resolveStaffTeamInvite(inv.id, status);
        athleteInviteQ.setRows(prev => prev.filter(i => i.id !== inv.id));
        void loadCounts();
        showSuccess(status === 'approved' ? t.admin.athleteInviteApproved : t.admin.athleteInviteRejected);
      } catch (err) { showError(err); }
    });
  }

  function handleAthleteClaim(claimId: string, status: 'approved' | 'rejected') {
    return resolving.run(`athleteClaim:${claimId}`, async () => {
      const recordId = athleteClaimQ.rows.find(c => c.id === claimId)?.athlete_record_id;
      try {
        await resolveClaimRequest(claimId, status);
        // #569 — an approval also rejects the rival claims of the record.
        athleteClaimQ.setRows(prev => prev.filter(c => c.id !== claimId && !(status === 'approved' && c.athlete_record_id === recordId)));
        void loadCounts();
        showSuccess(status === 'approved' ? t.admin.athleteClaimApproved : t.admin.athleteClaimRejected);
      } catch (err) {
        // #569 — the approval of a rival claim resolved this claim meanwhile.
        const code = errorCode(err);
        if (code === 'ALREADY_RESOLVED') { athleteClaimQ.setRows(prev => prev.filter(c => c.id !== claimId)); void loadCounts(); }
        showError(err);
      }
    });
  }

  function handleAthleteRequest(req: AthleteTeamRequest, status: 'approved' | 'rejected') {
    return resolving.run(`athleteJoin:${req.id}`, async () => {
      try {
        await resolveAthleteRequest(req.team_id, req.id, status);
        athleteJoinQ.setRows(prev => prev.filter(r => r.id !== req.id));
        void loadCounts();
        showSuccess(status === 'approved' ? t.admin.athleteRequestApproved : t.admin.athleteRequestRejected);
      } catch (err) { showError(err); }
    });
  }

  function handlePendingMember(memberId: string, status: 'approved' | 'rejected') {
    return resolving.run(`member:${memberId}`, async () => {
      try {
        await resolveAdminPendingMember(memberId, status);
        memberQ.setRows(prev => prev.filter(m => m.id !== memberId));
        void loadCounts();
        showSuccess(status === 'approved' ? t.admin.memberApproved : t.admin.memberRejected);
      } catch (err) { showError(err); }
    });
  }

  function handleTeamClaim(claimId: string, status: 'approved' | 'rejected') {
    return resolving.run(`teamClaim:${claimId}`, async () => {
      try {
        await resolveTeamClaim(claimId, status);
        teamClaimQ.setRows(prev => prev.filter(c => c.id !== claimId));
        void loadCounts();
        showSuccess(status === 'approved' ? t.admin.teamClaimApproved : t.admin.teamClaimRejected);
      } catch (err) { showError(err); }
    });
  }

  function handleRequest(id: string, status: 'approved' | 'rejected') {
    return resolving.run(`role:${id}`, async () => {
      try {
        await api.patch(`/api/users/admin/role-requests/${id}`, { status });
        roleHistoryQ.setRows(prev => prev.map(r => r.id === id ? { ...r, status } : r));
        roleQ.setRows(prev => prev.filter(r => r.id !== id));
        void loadCounts();
        showSuccess(status === 'approved' ? t.admin.requestApproved : t.admin.requestRejected);
        void usersQ.reload();
      } catch (err) {
        showError(err);
      }
    });
  }

  // GDPR erasure (#367). Hard delete → the label says "Delete" and asks twice.
  // The athlete record survives, unclaimed; a user who officiated a bout is
  // refused by the API (409 USER_HAS_OFFICIAL_RECORD), translated by apiError.
  async function eraseUser() {
    if (!editingUser) return;
    const userId = editingUser.id;
    // #552 — the hint was a block above Save; it is now the first question.
    if (!(await confirm({ message: `${t.admin.eraseUserHint}\n\n${t.admin.eraseConfirm1}`, confirmLabel: t.common.delete, destructive: true }))) return;
    if (!(await confirm({ message: t.admin.eraseConfirm2, confirmLabel: t.common.delete, destructive: true }))) return;
    await userEdit.run(async () => {
      try {
        await api.delete(`/api/users/admin/users/${userId}`);
        setUsers(prev => prev.filter(u => u.id !== userId));
        setEditingUser(null);
        showSuccess(t.admin.userErased);
      } catch (err) {
        showError(err);
      }
    });
  }

  async function saveUser() {
    if (!editingUser) return;
    const userId = editingUser.id;
    await userEdit.run(async () => {
      try {
        const res = await api.patch(`/api/users/admin/users/${userId}`, { ...editForm, roles: editRoles });
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...res.data } : u));
        setEditingUser(null);
        showSuccess(t.admin.userUpdated);
      } catch (err) {
        showError(err);
      }
    });
  }

  async function createUser() {
    await userCreate.run(async () => {
      try {
        const res = await api.post('/api/users/admin/create', createForm);
        // #552 — first: the list renders the first 100 rows, so a new user at
        // the end was not on the screen.
        setUsers(prev => [res.data, ...prev]);
        setShowCreateUser(false);
        setCreateForm({ email: '', first_name: '', last_name: '', password: '', phone: '', roles: [] });
        showSuccess(t.admin.userCreated.replace('{email}', res.data.email));
      } catch (err) {
        showError(err);
      }
    });
  }

  function startEditUser(u: UserListRow) {
    setEditingUser(u);
    setEditRoles([...u.roles]);
    setEditForm({
      first_name: u.first_name || '', last_name: u.last_name || '',
      email: u.email || '',
      phone: u.phone || '',
    });
  }

  function toggleRole(role: string) {
    setEditRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  }

  const filteredUsers = users.filter(u => {
    // The name in either order (#479), or the email (#661, F30).
    const matchesSearch = nameMatches(userSearch, u.first_name, u.last_name) ||
      textMatches(userSearch, u.email);
    const matchesRole = !roleFilter || (roleFilter === '__none' ? (!u.roles || u.roles.length === 0) : u.roles?.some(r => r === roleFilter));
    return matchesSearch && matchesRole;
  });

  // ── The queues (#376, #658, #664) ───────────────────────────────────────────
  // Every kind of pending request has ONE entry in this table: its list, the
  // card of its rows, its place in the switcher and its empty state. The
  // merged list, the switcher, the loading flag and the failed flag of the
  // default view all read the table, so a new kind is a new entry. The server
  // counter (`GET /api/users/admin/pending-counts`) has one key for each entry.
  // The default view answers "what needs me now" in one list: the identity,
  // the ask and the two buttons, oldest first (the longest wait is the most
  // urgent).
  // #569 — the claims of other persons for the same athlete record.
  const hasRival = (c: AdminAthleteClaim) =>
    athleteClaimQ.rows.some(o => o.id !== c.id && o.athlete_record_id === c.athlete_record_id);
  const claimMarks = (c: AdminAthleteClaim) => (
    <>
      <ClaimCheckBadge verified={c.birth_date_verified} />
      {hasRival(c) && <span data-testid="rival-claim" className={`badge badge-amber ${BADGE_XS}`}>{t.teams.rivalClaim}</span>}
    </>
  );
  const rq = t.admin.requestKinds;
  // The card of a role request. The Roles sub-tab draws it for every status.
  const roleRow = (r: RoleRequestRow): RequestRow => ({
    key: `role:${r.id}`, at: r.requested_at,
    first: r.first_name, last: r.last_name, email: r.email, photo: r.photo_url ?? undefined,
    summary: `${rq.wantsRole} ${roleLabel(r.requested_role)}`,
    marks: r.current_roles?.length ? <>{r.current_roles.map(role => <RoleBadge key={role} role={role} />)}</> : undefined,
    details: r.note ? <span className="italic">"{r.note}"</span> : undefined,
    status: r.status, resolvedAt: r.resolved_at ?? undefined,
    resolve: status => handleRequest(r.id, status),
  });
  const QUEUES: QueueDef[] = [
    queueDef({
      kind: 'role', kindLabel: rq.role, section: 'roles', sectionLabel: t.admin.roleRequests, adminOnly: true,
      queue: roleQ, toRow: roleRow,
    }),
    queueDef({
      kind: 'teamClaim', kindLabel: rq.teamClaim, section: 'claims', sectionLabel: t.admin.teamClaims,
      queue: teamClaimQ,
      toRow: (c): RequestRow => ({
        key: `teamClaim:${c.id}`, at: c.requested_at,
        first: c.first_name, last: c.last_name, email: c.email, photo: c.photo_url,
        summary: `${rq.claimsTeam} ${c.team_name}${c.requested_role ? ` (${roleLabel(c.requested_role)})` : ''}`,
        resolve: status => handleTeamClaim(c.id, status),
      }),
      empty: { icon: <PeopleIcon className="w-12 h-12" />, title: rq.emptyTeamClaims, hint: t.common.empty.claimsHint },
    }),
    queueDef({
      kind: 'member', kindLabel: rq.member, section: 'members', sectionLabel: t.admin.coachJoins,
      queue: memberQ,
      toRow: (m): RequestRow => ({
        key: `member:${m.id}`, at: m.joined_at,
        first: m.first_name, last: m.last_name, email: m.email, photo: m.photo_url,
        summary: `${rq.joinsTeam} ${m.team_name} (${roleLabel(m.role)})`,
        resolve: status => handlePendingMember(m.id, status),
      }),
      empty: { icon: <PeopleIcon className="w-12 h-12" />, title: rq.emptyMembers, hint: t.common.empty.joinsHint },
    }),
    queueDef({
      kind: 'athleteJoin', kindLabel: rq.athleteJoin, section: 'athletes', sectionLabel: t.admin.athleteJoins,
      queue: athleteJoinQ,
      toRow: (a): RequestRow => ({
        key: `athleteJoin:${a.id}`, at: a.requested_at,
        first: a.first_name || '', last: a.last_name || '', email: a.requester_email, photo: a.photo_url,
        summary: `${rq.joinsAsAthlete} ${a.team_name ?? ''}`,
        details: [
          a.old_team_name ? t.teams.currentlyIn.replace('{team}', a.old_team_name) : '',
          a.gender ? genderLabel(a.gender) : '—',
          a.age != null ? ageShort(a.age) : '',
          weightText(a.current_weight_kg),
          a.experience_bouts != null ? boutCount(a.experience_bouts) : '',
        ].filter(Boolean).join(' · '),
        resolve: status => handleAthleteRequest(a, status),
      }),
      empty: { icon: <PersonIcon className="w-12 h-12" />, title: rq.emptyAthleteJoins, hint: t.common.empty.joinsHint },
    }),
    queueDef({
      kind: 'athleteClaim', kindLabel: rq.athleteClaim, section: 'athleteClaims', sectionLabel: rq.athleteClaim,
      queue: athleteClaimQ,
      toRow: (c): RequestRow => ({
        key: `athleteClaim:${c.id}`, at: c.requested_at,
        first: c.req_first, last: c.req_last, email: c.req_email, photo: c.photo_url,
        summary: `${rq.claimsRecord} ${personName(c.first_name, c.last_name)} (${c.team_name ?? rq.noTeam})`,
        marks: claimMarks(c), claimId: c.id,
        resolve: status => handleAthleteClaim(c.id, status),
      }),
      empty: { icon: <PersonIcon className="w-12 h-12" />, title: rq.emptyClaims, hint: t.common.empty.requestsHint },
    }),
    // #658 — a staff invitation has NO section: it shows in the merged list only.
    queueDef({
      kind: 'athleteInvite', kindLabel: rq.athleteInvite,
      queue: athleteInviteQ,
      toRow: (i): RequestRow => ({
        key: `athleteInvite:${i.id}`, at: i.created_at,
        first: i.first_name || '', last: i.last_name || '',
        summary: `${rq.invitedTo} ${i.team_name} (${i.old_team_name ?? rq.noTeam})`,
        resolve: status => handleAthleteInvite(i, status),
      }),
    }),
  ];
  // A team manager reads no role queue.
  const queues = QUEUES.filter(q => isAdmin || !q.adminOnly);
  const mergedRequests = queues.flatMap(q => q.rows)
    .sort((a, b) => String(a.at ?? '').localeCompare(String(b.at ?? '')));
  // The skeleton waits for every queue, so the default view does not show a short list first.
  const loadingMerged = queues.some(q => q.queue.loading);
  const failedMerged = queues.some(q => q.queue.failed);
  const sectionQueue = queues.find(q => q.section === requestsSection && q.empty);
  const totalPendingCount = counts?.total ?? mergedRequests.length;

  // Tab-driven fetches: each tab fires its own loads on activation. The
  // request sub-panels are conditionally rendered inside the requests tab,
  // but every queue is read when the tab opens (each sub-section is a
  // client-side toggle, so all the lists must be ready). Without this gate,
  // the fetches fired on initial mount while the user was on the default
  // `users` tab and resolved before the requests sub-panels ever mounted →
  // no skeleton on entry.
  function loadQueues() {
    void roleHistoryQ.reload();
    for (const q of QUEUES) void q.queue.reload();
    void loadCounts();
  }
  useEffect(() => {
    if (tab === 'requests') loadQueues();
  }, [tab]);
  // The number on the Requests tab shows before the tab opens. On the Requests
  // tab the effect above reads the counter with the queues.
  useEffect(() => {
    if (tab !== 'requests') void loadCounts();
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-black min-w-0">{t.admin.panelTitle}</h1>
        <span className="badge badge-gray text-xs whitespace-nowrap shrink-0">{t.admin.adminOnly}</span>
      </div>


      {/* Tabs */}
      <TabBar
        activeTab={tab}
        onChange={id => setTab(id as AdminTab)}
        tabs={[
          ...(isAdmin ? [{ id: 'users', label: usersLoaded ? `${t.admin.users} (${users.length})` : t.admin.users }] : []),
          {
            id: 'requests',
            label: (
              <>
                {t.admin.requests}
                <CountDot n={totalPendingCount} />
              </>
            ),
          },
          ...(isAdmin ? [{ id: 'audit', label: t.admin.auditLog }] : []),
        ]}
      />

      {/* ── UNIFIED REQUESTS TAB ───────────────────────────────────── */}
      {tab === 'requests' && (
        <div className="space-y-4">
          {/* Section switcher — Role Requests is admin-only (admin grants
               admin/TM/referee/jury powers; TMs cannot view or approve). One
               button for each queue that has a section (QUEUES). */}
          <div className="relative">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto no-scrollbar sm:flex-wrap sm:overflow-visible">
              <button onClick={() => setRequestsSection('all')} className={sectionButton(requestsSection === 'all')}>
                {rq.all}
                <CountDot n={totalPendingCount} />
              </button>
              {queues.filter(q => q.section).map(q => (
                <button key={q.kind} onClick={() => setRequestsSection(q.section!)} className={sectionButton(requestsSection === q.section)}>
                  {q.sectionLabel}
                  <CountDot n={q.rows.length} />
                </button>
              ))}
            </div>
            {/* A fade at the right edge on a phone: the strip has more sections. */}
            <div aria-hidden="true" className="sm:hidden pointer-events-none absolute inset-y-0 right-0 w-6 rounded-r-lg bg-linear-to-l from-gray-100" />
          </div>

          {/* ── The merged queue (#376) ── */}
          {requestsSection === 'all' && (
            <AsyncContent
              loading={loadingMerged}
              error={failedMerged}
              onRetry={loadQueues}
              isEmpty={mergedRequests.length === 0}
              skeleton={<Skeleton.List shape="section" count={3} />}
              empty={<EmptyState
                icon={<MailIcon className="w-12 h-12" />}
                title={rq.emptyTitle}
                hint={t.common.empty.requestsHint}
              />}
            >
              <RequestList rows={mergedRequests} showKind isBusy={resolving.isBusy} />
            </AsyncContent>
          )}

          {/* ── Role Requests section (admin-only): the history, with a status filter ── */}
          {requestsSection === 'roles' && isAdmin && (
            <div className="space-y-3">
              <div className="flex gap-2">
                {(['pending', 'approved', 'rejected', 'all'] as const).map(f => (
                  <button key={f} onClick={() => setReqFilter(f)} data-testid={`role-filter-${f}`}
                    className={`px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                      reqFilter === f
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {f === 'all' ? rq.all : requestStatusLabel(f, true)}
                  </button>
                ))}
              </div>

              <AsyncContent
                loading={roleHistoryQ.loading}
                error={roleHistoryQ.failed}
                onRetry={() => { void roleHistoryQ.reload(); void loadCounts(); }}
                isEmpty={roleHistoryQ.rows.length === 0}
                skeleton={<Skeleton.List shape="section" count={3} />}
                empty={<EmptyState
                  icon={<MailIcon className="w-12 h-12" />}
                  title={t.admin.noRequestsByStatus[reqFilter]}
                  hint={t.common.empty.requestsHint}
                />}
              >
                <RequestList rows={roleHistoryQ.rows.map(roleRow)} isBusy={resolving.isBusy} />
              </AsyncContent>
            </div>
          )}

          {/* ── The list of one kind: team claims, coach joins, athlete joins, athlete record claims ── */}
          {sectionQueue?.empty && (
            <AsyncContent
              loading={sectionQueue.queue.loading}
              error={sectionQueue.queue.failed}
              onRetry={() => { void sectionQueue.queue.reload(); void loadCounts(); }}
              isEmpty={sectionQueue.rows.length === 0}
              skeleton={<Skeleton.List shape="section" count={2} />}
              empty={<EmptyState icon={sectionQueue.empty.icon} title={sectionQueue.empty.title} hint={sectionQueue.empty.hint} />}
            >
              <RequestList rows={sectionQueue.rows} isBusy={resolving.isBusy} />
            </AsyncContent>
          )}
        </div>
      )}

      {/* ── AUDIT LOG TAB ────────────────────────────────────────────── */}
      {tab === 'audit' && (
        <div className="space-y-4">
          <FilterBar
            search={auditSearchInput}
            onSearchChange={setAuditSearchInput}
            searchPlaceholder={t.admin.searchAudit}
            selects={[{
              value: auditTable,
              onChange: v => { setAuditTable(v); setAuditPage(0); },
              placeholder: t.admin.allTables,
              options: auditTables.map(t => ({ value: t, label: t })),
              className: 'input flex-1 min-w-32 sm:min-w-0 sm:flex-none sm:w-48',
            }]}
            actions={<>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                <input type="checkbox" checked={auditHideRefreshes} data-testid="audit-hide-refreshes"
                  onChange={e => { setAuditHideRefreshes(e.target.checked); setAuditPage(0); }} />
                {t.admin.auditHideRefreshes}
              </label>
              <span className="text-xs text-gray-400">{entryCount(auditTotal)}</span>
            </>}
          />
          <div className="card overflow-hidden">
            <AsyncContent
              loading={auditQ.loading}
              error={!!auditQ.error}
              onRetry={auditQ.reload}
              isEmpty={auditLog.length === 0}
              skeleton={
                <div className="divide-y divide-gray-100">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="px-4 py-3"><Skeleton.Bar className="h-3 w-full" /></div>
                  ))}
                </div>
              }
              empty={<EmptyState
                compact
                icon={<BookIcon className="w-10 h-10" />}
                title={t.common.empty.auditTitle}
                hint={auditFilterOn
                  ? t.common.empty.auditFilteredHint.replace('{option}', t.admin.auditHideRefreshes)
                  : t.common.empty.auditHint}
              />}
            >
              <div className="divide-y divide-gray-100 text-xs">
                {auditLog.map(a => (
                  <div key={a.id} className="px-4 py-3 space-y-1 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setAuditDetail(a)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`badge ${BADGE_XS} px-1.5 ${
                        a.action === 'INSERT' ? 'badge-green' : a.action === 'DELETE' ? 'bg-red-100 text-red-700 border-red-200' : 'badge-amber'
                      }`}>{a.action}</span>
                      <span className="font-semibold text-gray-700">{a.table_name}</span>
                      {/* #503 — the event says what happened: `sign_in_failed` and
                          `sign_in_succeeded` are both an INSERT. */}
                      {a.event && <span className="text-gray-500 font-mono text-[10px]" data-testid="audit-event">{a.event}</span>}
                      {a.row_label && <span className="text-gray-600 truncate max-w-[45%]" title={a.row_id}>{a.row_label}</span>}
                      <span className="text-gray-400">by</span>
                      <span className="font-medium">{a.user_email}</span>
                      <span className="ml-auto text-gray-400">{dayTime(a.created_at)}</span>
                    </div>
                    {a.action === 'UPDATE' && a.old_values && a.new_values && (() => {
                      const changes: string[] = [];
                      for (const key of Object.keys(a.new_values)) {
                        if (NOT_A_CHANGE.has(key)) continue;
                        const o = JSON.stringify(a.old_values[key]);
                        const n = JSON.stringify(a.new_values[key]);
                        if (o !== n) changes.push(`${key}: ${o} → ${n}`);
                      }
                      return changes.length > 0 ? (
                        <div className="text-[11px] text-gray-500 bg-gray-50 rounded-sm px-2 py-1 font-mono whitespace-pre-wrap">
                          {changes.join('\n')}
                        </div>
                      ) : null;
                    })()}
                    {a.action === 'INSERT' && a.new_values && (
                      <div className="text-[11px] text-gray-500 bg-green-50 rounded-sm px-2 py-1 font-mono truncate">
                        {Object.entries(a.new_values).filter(([k]) => !['id', 'created_at', 'updated_at'].includes(k)).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(', ')}
                      </div>
                    )}
                    {a.action === 'DELETE' && a.old_values && (
                      <div className="text-[11px] text-gray-500 bg-red-50 rounded-sm px-2 py-1 font-mono truncate">
                        {Object.entries(a.old_values).filter(([k]) => !['id', 'created_at', 'updated_at'].includes(k)).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </AsyncContent>
          </div>
          {/* Audit detail modal */}
          {auditDetail && (
            // #552 — a read-only view: the shared header (the table, the row,
            // the action) and one Close. The values do not scroll inside the
            // dialog that scrolls.
            <ModalBackdrop onClose={() => setAuditDetail(null)} maxWidth="lg" ariaLabel={t.admin.auditDetailAria}>
              <ModalHeader
                title={auditDetail.table_name}
                subtitle={(
                  <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                    {auditDetail.row_label && <span>{auditDetail.row_label}</span>}
                    <span className={`badge text-xs py-0.5 px-2 ${
                      auditDetail.action === 'INSERT' ? 'badge-green' : auditDetail.action === 'DELETE' ? 'bg-red-100 text-red-700 border-red-200' : 'badge-amber'
                    }`}>{auditDetail.action}</span>
                  </span>
                )}
              />
              <div className="text-xs space-y-2 mb-4">
                <div><span className="text-gray-400">{t.admin.auditBy}:</span> {auditDetail.user_email}</div>
                <div><span className="text-gray-400">{t.admin.auditWhen}:</span> {dayTime(auditDetail.created_at)}</div>
                <div><span className="text-gray-400">{t.admin.auditIp}:</span> {auditDetail.ip_address}</div>
                {auditDetail.row_label && <div><span className="text-gray-400">{t.admin.auditRow}:</span> <span className="font-medium">{auditDetail.row_label}</span></div>}
                <div><span className="text-gray-400">{t.admin.auditRowId}:</span> <span className="font-mono text-[10px] break-all">{auditDetail.row_id}</span></div>
              </div>
              {auditDetail.action === 'UPDATE' && auditDetail.old_values && auditDetail.new_values && (() => {
                const oldVals = auditDetail.old_values;
                const newVals = auditDetail.new_values;
                return (
                <div className="space-y-1">
                  <div className="text-xs font-semibold text-gray-500 uppercase">{t.admin.auditChanges}</div>
                  <div className="text-xs font-mono bg-gray-50 rounded-sm p-3 space-y-1 break-words">
                    {Object.keys(newVals).map(key => {
                      if (NOT_A_CHANGE.has(key)) return null;
                      const o = JSON.stringify(oldVals[key], null, 2);
                      const n = JSON.stringify(newVals[key], null, 2);
                      if (o === n) return null;
                      return <div key={key}><span className="text-gray-400">{key}:</span> <span className="text-red-500 line-through">{o}</span> → <span className="text-green-600">{n}</span></div>;
                    })}
                  </div>
                </div>
                );
              })()}
              {auditDetail.action === 'UPDATE' && !auditDetail.old_values && auditDetail.new_values && (
                // An operation event (start day, reassign, reorder) has no
                // row to diff; it carries a summary. Before #422 the panel
                // showed nothing at all for it.
                <div className="space-y-1">
                  <div className="text-xs font-semibold text-gray-500 uppercase">{t.admin.auditValues}</div>
                  <div className="text-xs font-mono bg-gray-50 rounded-sm p-3 space-y-1 break-words">
                    {Object.entries(auditDetail.new_values).map(([k, v]) => (
                      <AuditValue key={k} k={k} v={v} labels={auditDetail.labels} />
                    ))}
                  </div>
                </div>
              )}
              {auditDetail.action === 'INSERT' && auditDetail.new_values && (
                <div className="space-y-1">
                  <div className="text-xs font-semibold text-gray-500 uppercase">{t.admin.auditCreatedValues}</div>
                  <div className="text-xs font-mono bg-green-50 rounded-sm p-3 space-y-1 break-words">
                    {Object.entries(auditDetail.new_values).map(([k, v]) => (
                      <AuditValue key={k} k={k} v={v} labels={auditDetail.labels} />
                    ))}
                  </div>
                </div>
              )}
              {auditDetail.action === 'DELETE' && auditDetail.old_values && (
                <div className="space-y-1">
                  <div className="text-xs font-semibold text-gray-500 uppercase">{t.admin.auditDeletedValues}</div>
                  <div className="text-xs font-mono bg-red-50 rounded-sm p-3 space-y-1 break-words">
                    {Object.entries(auditDetail.old_values).map(([k, v]) => (
                      <AuditValue key={k} k={k} v={v} labels={auditDetail.labels} />
                    ))}
                  </div>
                </div>
              )}
              <ModalFooter onCancel={() => setAuditDetail(null)} cancelLabel={t.common.close} />
            </ModalBackdrop>
          )}
          {auditTotal > 100 && (
            <div className="flex items-center justify-center gap-3">
              <button disabled={auditPage === 0} onClick={() => setAuditPage(p => p - 1)} className="btn-outline btn-sm"><ChevronLeftIcon className="w-3.5 h-3.5" />{t.admin.pagePrev}</button>
              <span className="text-xs text-gray-500">{t.admin.pageOf.replace('{n}', String(auditPage + 1)).replace('{m}', String(Math.ceil(auditTotal / 100)))}</span>
              <button disabled={(auditPage + 1) * 100 >= auditTotal} onClick={() => setAuditPage(p => p + 1)} className="btn-outline btn-sm">{t.admin.pageNext}<ChevronRightIcon className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>
      )}

      {/* ── USERS TAB ──────────────────────────────────────────────────── */}
      {tab === 'users' && (
        <div className="space-y-4">
          {/* Search + filter + create */}
          <FilterBar
            search={userSearch}
            onSearchChange={setUserSearch}
            searchPlaceholder={t.admin.searchUsers}
            selects={[{
              value: roleFilter,
              onChange: setRoleFilter,
              placeholder: t.admin.allRoles,
              options: [
                ...ALL_ROLES.map(r => ({ value: r, label: roleLabel(r) })),
                { value: '__none', label: t.admin.noRoles },
              ],
              className: 'input flex-1 min-w-32 sm:min-w-0 sm:flex-none sm:w-40',
            }]}
            actions={isAdmin ? (
              <button onClick={() => setShowCreateUser(true)} className="btn-primary btn-sm">+ {t.common.create}</button>
            ) : undefined}
          />

          {/* Create user modal */}
          {showCreateUser && (
            <ModalBackdrop onClose={() => setShowCreateUser(false)} ariaLabel={t.admin.createUserTitle}>
              <ModalHeader title={t.admin.createUserTitle} />
              {/* #552 — a form: Enter sends it. */}
              <form onSubmit={e => { e.preventDefault(); createUser(); }}>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {/* Field draws the mark and sets `required` on each name. */}
                  <Field label={t.profile.firstName} required>
                    <input className="input" value={createForm.first_name} onChange={e => setCreateForm(f => ({ ...f, first_name: e.target.value }))} />
                  </Field>
                  <Field label={t.profile.lastName} required>
                    <input className="input" value={createForm.last_name} onChange={e => setCreateForm(f => ({ ...f, last_name: e.target.value }))} />
                  </Field>
                </div>
                <Field label={t.home.email} required>
                  <input type="email" className="input" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} />
                </Field>
                <Field label={t.home.password}>
                  <input type="password" className="input" placeholder="" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} />
                </Field>
                <Field label={t.profile.phone}>
                  <input className="input" value={createForm.phone} onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))} />
                </Field>
                <Field label={t.admin.roles} group>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {ALL_ROLES.map(role => (
                      <label key={role} className="flex items-center gap-1.5 text-sm py-1.5 cursor-pointer">
                        <input type="checkbox" checked={createForm.roles.includes(role)}
                          onChange={() => setCreateForm(f => ({ ...f, roles: f.roles.includes(role) ? f.roles.filter(r => r !== role) : [...f.roles, role] }))}
                          className="w-4 h-4 rounded-sm accent-gray-900" />
                        <span>{roleLabel(role)}</span>
                      </label>
                    ))}
                  </div>
                </Field>
              </div>
              <ModalFooter saveAsSubmit busy={userCreate.busy} saveLabel={t.common.create} onCancel={() => setShowCreateUser(false)} />
              </form>
            </ModalBackdrop>
          )}

          {/* #664 (F40) — the one ping modal. The title names the admin, in the language of the admin (#557, bug 39). */}
          {pingUser && (
            <PingModal target={pingUser} title={t.roles.admin} defaultText={t.admin.pingDefault} onClose={() => setPingUser(null)} />
          )}

          {/* Edit user modal */}
          {editingUser && (
            <ModalBackdrop onClose={() => setEditingUser(null)} ariaLabel={t.admin.editUserTitle}>
              {/* #552, #675 — the GDPR erasure is a text button in the footer
                  (a hard delete: two confirms). */}
              <ModalHeader title={t.admin.editUserTitle} subtitle={editingUser.email} />
              <div className="space-y-3">
                <p className="text-xs text-gray-500">{t.admin.editUserNote}</p>
                <div className="grid grid-cols-2 gap-3">
                  {/* #455 — both names are required: the backend refuses an
                      empty one (#447). A Google sign-in with a one-word name
                      has no last name, so say it here, not after the save. */}
                  {/* The error of the Field is the line "Required". The input names it in aria-describedby. */}
                  <Field label={t.profile.firstName} required error={!editForm.first_name.trim() ? t.common.required : undefined}>
                    <input id="admin-edit-first-name" className="input" value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} />
                  </Field>
                  <Field label={t.profile.lastName} required error={!editForm.last_name.trim() ? t.common.required : undefined}>
                    <input id="admin-edit-last-name" className="input" value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} />
                  </Field>
                </div>
                {/* The server refuses an empty email (users.ts notEmpty). */}
                <Field label={t.home.email} required>
                  <input type="email" className="input" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                </Field>
                <Field label={t.profile.phone}>
                  <input className="input" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                </Field>
                <Field label={t.admin.roles} group>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {ALL_ROLES.map(role => (
                      <label key={role} className="flex items-center gap-1.5 text-sm py-1.5 cursor-pointer">
                        <input type="checkbox" checked={editRoles.includes(role)}
                          onChange={() => toggleRole(role)}
                          className="w-4 h-4 rounded-sm accent-gray-900" />
                        <span>{roleLabel(role)}</span>
                      </label>
                    ))}
                  </div>
                </Field>
              </div>
              <ModalFooter onSave={saveUser} onCancel={() => setEditingUser(null)}
                actions={[{ label: t.admin.eraseUser, onClick: eraseUser, destructive: true, disabled: userEdit.busy, testId: 'user-erase' }]}
                saveDisabled={!editForm.first_name.trim() || !editForm.last_name.trim()}
                busy={userEdit.busy} />
            </ModalBackdrop>
          )}

          {/* User list */}
          <div className="card overflow-hidden">
            <AsyncContent
              loading={usersQ.loading}
              error={!!usersQ.error}
              onRetry={usersQ.reload}
              isEmpty={filteredUsers.length === 0}
              skeleton={
                <div className="divide-y divide-gray-100">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="px-4 py-3 flex items-center gap-3">
                      <Skeleton.Circle className="w-10 h-10" />
                      <div className="flex-1"><Skeleton.Bar className="h-3 w-1/3" /></div>
                    </div>
                  ))}
                </div>
              }
              empty={<EmptyState
                compact
                icon={<PersonIcon className="w-10 h-10" />}
                title={t.common.empty.usersTitle}
                hint={t.common.empty.usersHint}
              />}
            >
            <div className="divide-y divide-gray-100">
              {filteredUsers.slice(0, shownUsers).map(u => (
                <div key={u.id} className="px-4 py-3 flex items-center gap-3">
                  <Avatar
                    firstName={u.first_name}
                    lastName={u.last_name}
                    photoUrl={u.photo_url}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {personName(u.first_name, u.last_name)}
                    </div>
                    <div className="text-xs text-gray-400 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {u.roles?.map((r: string) => (
                        <RoleBadge key={r} role={r} />
                      ))}
                      {u.has_password && <span className="badge bg-blue-50 text-blue-700 border-blue-200">Email</span>}
                      {u.has_google && <span className="badge bg-red-50 text-red-700 border-red-200">Google</span>}
                      {!u.has_password && !u.has_google && <span className="badge bg-gray-50 text-gray-400 border-gray-200">{t.admin.noSignIn}</span>}
                      {u.created_at && <span className="badge bg-gray-50 text-gray-400 border-gray-200">{day(u.created_at)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isAdmin && (
                      <button
                        onClick={() => setPingUser(u)} data-testid={`user-ping-${u.id}`}
                        className="text-gray-400 hover:text-amber-500 hover:bg-gray-100 rounded-md transition-colors p-2.5"
                        title={t.admin.notifyUser.replace('{name}', personName(u.first_name, u.last_name))}
                        aria-label={t.admin.notifyUser.replace('{name}', personName(u.first_name, u.last_name))}>
                        <BellIcon className="w-4 h-4" filled />
                      </button>
                    )}
                    <button
                      onClick={() => startEditUser(u)}
                      className="btn-icon"
                      title={t.admin.editUser} aria-label={t.admin.editUser}>
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {filteredUsers.length > shownUsers && (
              <div className="p-3 border-t border-gray-100">
                <button type="button" onClick={() => setShownUsers(n => n + USERS_PAGE_SIZE)}
                  data-testid="admin-users-show-more" className="btn-outline btn-sm w-full">
                  {t.admin.showMore.replace('{shown}', String(shownUsers)).replace('{total}', String(filteredUsers.length))}
                </button>
              </div>
            )}
            </AsyncContent>
          </div>
        </div>
      )}
    </div>
  );
}
